/**
 * 概览「商家基本资料」· 「开通分销系统 / 添加分销系统管理」按钮（主人 2026-09-14 定案）。
 *
 * 二维码即身份：扫码人成为**本组织**的分销系统管理员。会话引擎复用商家门户扫码
 * （`intent=dist_admin`），与登录二维码同一套 wxacode 页面，扫码端体验一致。
 *
 * 两条纪律与「分销管理平台」按钮一致：
 * - **显隐与文案只由后端裁定**（`GET /merchant/dist-admin/status`：
 *   `is_admin` 决定显不显示，`button_text` 决定叫「开通」还是「添加」），
 *   前端不做二次推断，避免两处口径漂移。
 * - 探测/生成失败一律**不抛错**：按钮是可选的增强入口，出错只保持隐藏或就地提示，
 *   不挡概览页其余内容。
 */
import { $, bindClick } from "../lib/dom.js";
import { api } from "../api.js";
import { openOverlay, closeOverlay } from "../lib/modal-scroll.js";

const POLL_MS = 3000;

let pollTimer = null;
let sessionId = "";

function setStatus(text, isErr) {
  const el = $("dist-open-status");
  if (!el) return;
  el.textContent = text || "";
  el.classList.toggle("hidden", !text);
  el.classList.toggle("err", !!isErr);
}

function setQrLoading(on) {
  const frame = $("dist-open-qr-frame");
  if (frame) frame.classList.toggle("is-loading", !!on);
  const img = $("dist-open-qr-img");
  if (img && on) img.classList.add("hidden");
}

function applyQr(dataUrl) {
  const img = $("dist-open-qr-img");
  const box = $("dist-open-modal");
  if (box) openOverlay(box);
  if (!img) return;
  if (dataUrl) {
    img.src = dataUrl;
    img.classList.remove("hidden");
  } else {
    img.removeAttribute("src");
    img.classList.add("hidden");
  }
}

function stopPoll() {
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
}

function closeModal() {
  stopPoll();
  sessionId = "";
  const box = $("dist-open-modal");
  if (box) closeOverlay(box);
  setStatus("");
}

/** 按钮态：显隐与文案均来自后端；失败保持隐藏。 */
export async function renderDistOpenButton() {
  const btn = $("btn-dist-open");
  if (!btn) return;
  btn.classList.add("hidden");
  let data = null;
  try {
    const res = await api("GET", "/api/v1/merchant/dist-admin/status");
    if (res.ok) data = res.data;
  } catch {
    /* 探测失败：保持隐藏 */
  }
  if (!data || !data.is_admin) return;
  btn.textContent = data.button_text || "开通分销系统";
  btn.classList.remove("hidden");
}

async function startQr() {
  const btn = $("btn-dist-open");
  const title = $("dist-open-title");
  if (title) title.textContent = btn ? btn.textContent : "开通分销系统";
  setQrLoading(true);
  setStatus("正在生成二维码…");
  const mockBtn = $("btn-dist-open-mock-scan");
  if (mockBtn) mockBtn.classList.add("hidden");
  applyQr("");
  let data = null;
  try {
    const res = await api("POST", "/api/v1/merchant/dist-admin/qr/start", {
      body: { provider: "wechat" },
    });
    if (res.ok) data = res.data;
    else {
      setQrLoading(false);
      setStatus(errMsg(res.data, "无法生成二维码，请稍后重试"), true);
      return;
    }
  } catch {
    setQrLoading(false);
    setStatus("无法生成二维码，请稍后重试", true);
    return;
  }
  setQrLoading(false);
  sessionId = (data && data.session_id) || "";
  if (!sessionId) {
    setStatus("二维码会话创建失败，请重试", true);
    return;
  }
  applyQr(data.qr_image_data_url || "");
  if (mockBtn) mockBtn.classList.toggle("hidden", data.mode !== "mock");
  setStatus("请扫码人用微信扫一扫");
  startPoll();
}

function startPoll() {
  stopPoll();
  pollOnce();
  pollTimer = setInterval(pollOnce, POLL_MS);
}

async function pollOnce() {
  if (!sessionId) return stopPoll();
  let data = null;
  try {
    const res = await api(
      "GET",
      `/api/v1/merchant/dist-admin/qr/${encodeURIComponent(sessionId)}`
    );
    if (!res.ok) {
      // 过期会话会被服务端**整条回收**（不是回一个 status=expired），
      // 表现为 404 AUTH_SESSION_NOT_FOUND；不在这里收口就会永远静默轮询下去。
      stopPoll();
      setStatus(expiredHint(res.data), true);
      return;
    }
    data = res.data;
  } catch {
    return; // 网络抖动：下个周期再试，不打断
  }
  if (!data) return;
  if (data.status === "completed") {
    stopPoll();
    let msg = data.message || "分销系统管理员已设置";
    if (data.hq_ensured === false) {
      msg += "；分销服务暂不可用，请联系平台稍后重试";
    }
    // dist 角色没绑上时服务端已在 message 里写明，这里只把它一并算作「需重试」态
    // （与 hq_ensured 同构：两项都是顺带做成的事，失败不挡会话 completed）。
    setStatus(msg, data.hq_ensured === false || data.dist_admin_ok === false);
    setTimeout(() => {
      closeModal();
      // 按钮随之改名为「添加分销系统管理」；操作人若因此获得 dist 许可，
      // 页头「分销管理平台」按钮也会跟着出现——两者都靠 refreshAll 重算
      afterDone();
    }, 1200);
    return;
  }
  if (data.status === "expired") {
    stopPoll();
    setStatus("二维码已过期，请点「刷新二维码」重新生成", true);
    return;
  }
  if (data.status === "failed") {
    stopPoll();
    setStatus(friendlyError(data), true);
    return;
  }
  if (data.status === "scanned") setStatus("已扫码，正在处理…");
}

/** 非 2xx 轮询：过期/不存在一律提示刷新二维码。 */
function expiredHint(data) {
  const code = errCode(data);
  if (code === "AUTH_SESSION_EXPIRED" || code === "AUTH_SESSION_NOT_FOUND") {
    return "二维码已过期，请点「刷新二维码」重新生成";
  }
  return errMsg(data, "二维码已失效，请点「刷新二维码」重新生成");
}

function errCode(data) {
  return (
    (data && data.error_code) ||
    (data && data.error && data.error.code) ||
    ""
  );
}

function errMsg(data, fallback) {
  return (
    (data && data.message) ||
    (data && data.error && data.error.message) ||
    fallback
  );
}

function friendlyError(data) {
  const code = errCode(data);
  if (code === "PERSON_RESOLVE_FAILED") return "未能识别扫码用户身份，请重试";
  if (code === "DIST_GRANT_FAILED") return "开通失败，请稍后重试";
  return errMsg(data, "开通失败，请稍后重试");
}

/* 完成后由 main.js 注入的刷新回调（避免与 panels/index.js 循环引用） */
let _onDone = null;

function afterDone() {
  if (_onDone) _onDone();
}

export function initDistOpenHandlers(refreshAll) {
  _onDone = typeof refreshAll === "function" ? refreshAll : null;
  bindClick("btn-dist-open", () => startQr());
  bindClick("btn-dist-open-refresh", () => startQr());
  bindClick("dist-open-close", () => closeModal());
  bindClick("btn-dist-open-mock-scan", async () => {
    if (!sessionId) return;
    setStatus("正在模拟扫码…");
    const { ok, data } = await api(
      "POST",
      `/api/v1/merchant/dist-admin/qr/${encodeURIComponent(sessionId)}/mock-scan`,
      { body: { nickname: "IT老民工" } }
    );
    if (ok && data) pollOnce();
    else setStatus(errMsg(data, "模拟扫码失败"), true);
  });
}
